import {
  DestroyRef,
  Injectable,
  NgZone,
  Renderer2,
  RendererFactory2,
  inject,
} from '@angular/core';
import { filter, switchMap } from 'rxjs/operators';
import { isNotUndefined, WindowRef } from '@boels/helpers';
import { SiteContextFacade } from '../../../site-context/facade/site-context.facade';
import { CmsFacade } from '../../../cms/facade/cms.facade';
import { Page, PageType } from '@boels/models';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { triggerLoad$ } from './trigger-load';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SmartEditService {
  protected router = inject(Router);
  protected siteContextFacade = inject(SiteContextFacade);
  protected cmsFacade = inject(CmsFacade);
  protected zone = inject(NgZone);
  protected winRef = inject(WindowRef);
  protected rendererFactory = inject(RendererFactory2);
  protected destroyRef = inject(DestroyRef);

  private isPreviewPage = false;

  private defaultPreviewProductCode: string | undefined;
  private defaultPreviewCategoryCode: string | undefined;

  constructor() {
    const winRef = this.winRef;

    // load webApplicationInjector.js first
    // this.loadScript();

    if (winRef.nativeWindow) {
      const window = winRef.nativeWindow as any;
      // rerender components and slots after editing
      window.smartedit = window.smartedit || {};
      window.smartedit.renderComponent = (componentId: string) =>
        this.renderComponent(componentId);

      // reprocess page
      window.smartedit.reprocessPage = this.reprocessPage;
    }
  }

  public processCmsPage(): void {
    this.siteContextFacade
      .getActiveBaseSiteEntity()
      .pipe(
        filter((site: any) => Boolean(site)),
        tap((site) => {
          this.defaultPreviewCategoryCode = site.defaultPreviewCategoryCode;
          this.defaultPreviewProductCode = site.defaultPreviewProductCode;
        }),
        switchMap(() => this.cmsFacade.getActivePage()),
        filter(isNotUndefined),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cmsPage) => {
        // before adding contract to page, we need redirect to that page
        this.goToPreviewPage(cmsPage);
        this.addPageContract(cmsPage);
      });
  }
  /**
   * add CSS classes in a body tag
   */
  protected addPageContract(cmsPage: Page) {
    const renderer = this.rendererFactory.createRenderer('body', null);
    const element = this.winRef.document.body;

    // remove old page contract
    const previousContract: string[] = [];
    Array.from(element.classList).forEach((attr) =>
      previousContract.push(attr),
    );
    previousContract.forEach((attr) => renderer.removeClass(element, attr));

    // add new page contract
    this.addSmartEditContract(element, renderer, cmsPage.properties);
  }

  /**
   * go to the default preview page
   */
  protected goToPreviewPage(cmsPage: Page) {
    // only the first page is the smartedit preview page
    if (!this.isPreviewPage) {
      this.isPreviewPage = true;
      if (
        cmsPage?.typeCode === PageType.PRODUCT_PAGE &&
        this.defaultPreviewProductCode
      ) {
        this.router.navigate([`p/${this.defaultPreviewProductCode}`]);
      } else if (
        cmsPage.typeCode === PageType.CATEGORY_PAGE &&
        this.defaultPreviewCategoryCode
      ) {
        this.router.navigate([`category/${this.defaultPreviewCategoryCode}`]);
      }
      if (cmsPage?.uid.includes('search')) {
        this.router.navigate([`search/find`]);
      }
    }
  }

  /**
   * re-render CMS components and slots
   */
  protected renderComponent(componentId: string): boolean {
    if (componentId) {
      this.zone.run(() => {
        // refresh the content because it's been updated in smart edit
        this.cmsFacade.resetCmsPage();
        triggerLoad$.next(true);
      });
    }

    return true;
  }

  protected reprocessPage() {
    // TODO: reprocess page API
  }

  /**
   * add smartedit HTML markup contract
   */
  public addSmartEditContract(
    element: Element,
    renderer: Renderer2,
    properties: any,
  ): void {
    if (properties) {
      // check each group of properties, e.g. smartedit
      Object.keys(properties).forEach((group) => {
        const name = 'data-' + group + '-';
        const groupProps = properties[group];

        // check each property in the group
        Object.keys(groupProps).forEach((propName) => {
          const propValue = groupProps[propName];
          if (propName === 'classes') {
            const classes = propValue.split(' ');
            classes.forEach((classItem: string) => {
              renderer.addClass(element, classItem);
            });
          } else {
            renderer.setAttribute(
              element,
              name +
                propName
                  .split(/(?=[A-Z])/)
                  .join('-')
                  .toLowerCase(),
              propValue,
            );
          }
        });
      });
    }
  }
}
